import React, { useState, useCallback, useMemo } from 'react';
import {
	EyeIcon,
	PaperAirplaneIcon,
	TrashIcon,
	PhotographIcon,
} from '@heroicons/react/outline';
import { UploadIcon } from './icons';
import { PreviewDevice } from './PreviewDevice';
import AppmakerForm from '@appmaker/core/components/AppmakerForm';
import appmaker from '@appmaker/core/index';
import { ExclamationIcon } from '@heroicons/react/solid';
import TestNotification from './TestNotification';
import { MediaPlaceholder } from 'packages/blockEditor/components/BlockEditor/components/MediaPlaceHolder';
import {
	useCreateNotification,
	useCreateScheduledNotification,
	useSendTestNotification
} from '../../hooks/useEngageApi';
import { toast } from '@appmaker/components/ReactHotToastIntegrator';
import { mapAction } from './helper';
import { isAdmin as checkIsAdmin } from '@appmaker/core/store/user';
import dayjs from 'dayjs';

const isAppleOS = (_window = window) => {
	const platform =
		_window.navigator.userAgentData?.platform || _window.navigator.platform;

	return (
		platform.indexOf('Mac') !== -1 ||
		['iPad', 'iPhone', 'macOS'].includes(platform)
	);
};

// Utility function to parse error messages and return user-friendly messages
const parseErrorMessage = (error) => {
	console.error('Full error object:', error);
	
	// Handle different error formats
	let errorData = null;
	
	// Check if error has response data
	if (error?.response?.data) {
		errorData = error.response.data;
	} else if (error?.data) {
		errorData = error.data;
	} else if (typeof error === 'string') {
		return error;
	} else if (error?.message) {
		// Check if the message is a JSON string
		try {
			errorData = JSON.parse(error.message);
		} catch {
			return error.message;
		}
	}
	
	// Parse the error data
	if (errorData) {
		// Handle the specific error format from the API
		if (errorData.error === 1 && errorData.status === 400) {
			switch (errorData.message) {
				case 'Invalid request body':
					return 'Please check your notification details and try again. Some required information may be missing or invalid.';
				case 'Missing required fields':
					return 'Please fill in all required fields (title and message are required).';
				case 'Invalid image URL':
					return 'The image URL provided is not valid. Please upload a new image or check the URL.';
				case 'Invalid schedule date':
					return 'Please select a valid future date and time for scheduling.';
				default:
					return errorData.message || 'Something went wrong while sending the notification.';
			}
		}
		
		// Handle other common error cases
		if (errorData.status === 403) {
			return 'You do not have permission to send notifications. Please contact your administrator.';
		}
		
		if (errorData.status === 429) {
			return 'Too many requests. Please wait a moment before trying again.';
		}
		
		if (errorData.status === 500) {
			return 'Our servers are experiencing issues. Please try again in a few moments.';
		}
		
		// Handle other error formats
		if (errorData.message) {
			return errorData.message;
		}
		
		if (errorData.error && typeof errorData.error === 'string') {
			return errorData.error;
		}
	}
	
	// Handle network errors
	if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
		return 'Request timed out. Please check your internet connection and try again.';
	}
	
	if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
		return 'Network error. Please check your internet connection and try again.';
	}
	
	// Fallback to generic message
	return 'Something went wrong while sending the notification. Please try again.';
};

// Memoized schedule types for better performance
const scheduleTypes = [
	{ value: 'once', label: 'Once' },
	{ value: 'daily', label: 'Daily' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'monthly', label: 'Monthly' },
];

// Optimized form field components
const TextInput = React.memo(({
	id,
	name,
	label,
	value,
	onChange,
	placeholder,
	required = false,
	helpText
}) => (
	<div>
		<label htmlFor={id} className="block text-sm font-medium text-gray-700">
			{label}
		</label>
		<input
			type="text"
			id={id}
			name={name}
			value={value}
			onChange={onChange}
			className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
			placeholder={placeholder}
			required={required}
		/>
		{helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
	</div>
));

TextInput.displayName = 'TextInput';

const TextArea = React.memo(({
	id,
	name,
	label,
	value,
	onChange,
	placeholder,
	rows = 4,
	required = false
}) => (
	<div>
		<label htmlFor={id} className="block text-sm font-medium text-gray-700">
			{label}
		</label>
		<textarea
			id={id}
			name={name}
			value={value}
			onChange={onChange}
			rows={rows}
			className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
			placeholder={placeholder}
			required={required}
		/>
	</div>
));

TextArea.displayName = 'TextArea';

const RadioGroup = React.memo(({ name, options, value, onChange, label }) => (
	<div>
		<label className="block text-sm font-medium text-gray-700 mb-3">
			{label}
		</label>
		<div className="space-x-6 flex flex-row">
			{options.map((option) => (
				<div key={option.value} className="flex items-center">
					<input
						id={option.value}
						name={name}
						type="radio"
						value={option.value}
						checked={value === option.value}
						onChange={onChange}
						className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
					/>
					<label
						htmlFor={option.value}
						className="ml-2 block text-sm font-medium text-gray-700"
					>
						{option.label}
					</label>
				</div>
			))}
		</div>
	</div>
));

RadioGroup.displayName = 'RadioGroup';

// Admin-only switch component
const AdminSwitch = React.memo(({ checked, onChange, label, description }) => (
	<div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
		<div className="flex-1">
			<h3 className="text-sm font-medium text-gray-900">{label}</h3>
			{description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
		</div>
		<div className="ml-4">
			<button
				type="button"
				className={`${
					checked ? 'bg-indigo-600' : 'bg-gray-200'
				} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
				role="switch"
				aria-checked={checked}
				onClick={() => onChange(!checked)}
			>
				<span
					aria-hidden="true"
					className={`${
						checked ? 'translate-x-5' : 'translate-x-0'
					} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
				/>
			</button>
		</div>
	</div>
));

AdminSwitch.displayName = 'AdminSwitch';

const PreviewSection = React.memo(({
	formData,
	dateRange,
	onTestNotification,
	onSendNotification,
	isTestLoading,
	isSendLoading
}) => (
	<div className="w-96 p-2">
		<h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
			Preview
		</h3>
		<PreviewDevice
			image={formData.image}
			title={formData.title}
			message={formData.message}
		/>
		{formData.sendType === 'schedule' &&
			formData.scheduleDate &&
			formData.scheduleTime && (
				<div className="mt-3 text-xs text-gray-500">
					Scheduled for:{' '}
					{new Date(
						`${formData.scheduleDate}T${formData.scheduleTime}`
					).toLocaleString()}
					{formData.scheduleType !== 'once' &&
						formData.scheduleEndDate && (
							<span>
								{' '}
								until{' '}
								{new Date(formData.scheduleEndDate).toLocaleDateString()}
							</span>
						)}
				</div>
			)}

		<div className="mt-6 space-x-3 flex flex-row">
			<button
				type="button"
				onClick={onTestNotification}
				disabled={isTestLoading}
				className="w-full inline-flex justify-center items-center rounded-md border border-gray-300 bg-white py-1 px-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
			>
				<EyeIcon className="h-4 w-4 mr-2" />
				{isTestLoading ? 'Sending...' : 'Test Notification'}
			</button>
			<button
				type="button"
				onClick={onSendNotification}
				disabled={isSendLoading}
				className="w-full inline-flex justify-center items-center rounded-md border border-transparent bg-indigo-600 py-1 px-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
			>
				<PaperAirplaneIcon className="h-4 w-4 mr-2 transform rotate-45" />
				{isSendLoading ? 'Sending...' : 'Send Notification'}
			</button>
		</div>
	</div>
));

PreviewSection.displayName = 'PreviewSection';

const NewNotification = ({ projectId }) => {
	const [formData, setFormData] = useState({
		title: '',
		message: '',
		sendType: 'immediately',
		scheduleType: 'once',
		scheduleDate: '',
		scheduleTime: '',
		scheduleEndDate: '',
		image: null,
		action: null,
	});
	const [isTestNotificationOpen, setIsTestNotificationOpen] = useState(false);
	const [formKey, setFormKey] = useState(0); // Key to force AppmakerForm remount
	const [isSubmitting, setIsSubmitting] = useState(false); // Prevent duplicate submissions
	const [keepFormAfterSending, setKeepFormAfterSending] = useState(false); // Admin-only switch to keep form data

	// Helper function to upload image and get URL
	const uploadImage = async (imageFile) => {
		if (!imageFile) return null;

		// TODO: Implement actual image upload to your storage service
		// For now, return a placeholder URL
		return URL.createObjectURL(imageFile);
	};

	// Helper function to format date and time for API
	const formatDateTime = (date, time) => {
		if (!date || !time) return null;

		const dateTimeString = `${date}T${time}`;
		const dateObj = dayjs(dateTimeString);
		
		return {
			date: dateObj.format('YYYY-MM-DD'), // 2025-06-12 format
			time: dateObj.format('HH:mm:ss') // 22:45:00 format
		};
	};

	// Helper function to validate scheduled date and time
	const validateScheduledDateTime = (date, time) => {
		if (!date || !time) {
			return { isValid: false, error: 'Please select both date and time' };
		}

		const dateTimeString = `${date}T${time}`;
		const scheduledDateTime = dayjs(dateTimeString);
		const now = dayjs();
		const minScheduledTime = now.add(15, 'minute');

		if (scheduledDateTime.isBefore(minScheduledTime)) {
			return { 
				isValid: false, 
				error: 'Scheduled time must be at least 15 minutes in the future' 
			};
		}

		return { isValid: true, error: null };
	};

	// Helper function to get minimum date (today)
	const getMinDate = () => {
		return dayjs().format('YYYY-MM-DD');
	};

	// Helper function to get minimum time for today
	const getMinTimeForDate = (date) => {
		const today = dayjs().format('YYYY-MM-DD');
		if (date === today) {
			// For today, add 15 minutes to current time
			return dayjs().add(15, 'minute').format('HH:mm');
		}
		return '00:00'; // For future dates, any time is fine
	};

	// Helper function to reset form data
	const resetFormData = useCallback(() => {
		setFormData({
			title: '',
			message: '',
			sendType: 'immediately',
			scheduleType: 'once',
			scheduleDate: '',
			scheduleTime: '',
			scheduleEndDate: '',
			image: null,
			action: null,
		});
		// Increment form key to force AppmakerForm remount
		setFormKey(prev => prev + 1);
		setIsSubmitting(false); // Reset submitting state
	}, []);

	// API mutation hooks with retry disabled
	const createNotificationMutation = useCreateNotification({
		onSuccess: useCallback((response) => {
			console.log('🎉 Notification sent successfully, showing toast...');
			const toastId = toast.success('Notification sent successfully!');
			console.log('Toast ID:', toastId);
			if (!keepFormAfterSending) {
				resetFormData();
			} else {
				setIsSubmitting(false); // Reset submitting state when keeping form data
			}
		}, [resetFormData, keepFormAfterSending]),
		onError: useCallback((error) => {
			console.log('❌ Error sending notification, showing error toast...');
			const toastId = toast.error(parseErrorMessage(error));
			console.log('Error toast ID:', toastId);
			console.error('Error sending notification:', error);
			setIsSubmitting(false); // Reset submitting state on error
		}, []),
		retry: false, // Disable automatic retry
	});

	const createScheduledNotificationMutation = useCreateScheduledNotification({
		onSuccess: useCallback((response) => {
			console.log('🎉 Scheduled notification created successfully, showing toast...');
			const toastId = toast.success('Scheduled notification created successfully!');
			console.log('Scheduled toast ID:', toastId);
			if (!keepFormAfterSending) {
				resetFormData();
			} else {
				setIsSubmitting(false); // Reset submitting state when keeping form data
			}
		}, [resetFormData, keepFormAfterSending]),
		onError: useCallback((error) => {
			console.log('❌ Error creating scheduled notification, showing error toast...');
			const toastId = toast.error(parseErrorMessage(error));
			console.log('Scheduled error toast ID:', toastId);
			console.error('Error creating scheduled notification:', error);
			setIsSubmitting(false); // Reset submitting state on error
		}, []),
		retry: false, // Disable automatic retry
	});

	const sendTestNotificationMutation = useSendTestNotification({
		onSuccess: useCallback((response) => {
			toast.success('Test notification sent successfully!');
			setIsTestNotificationOpen(false);
		}, []),
		onError: useCallback((error) => {
			toast.error(parseErrorMessage(error));
			console.error('Error sending test notification:', error);
		}, []),
		retry: false, // Disable automatic retry
	});

	// Memoized form handlers
	const handleInputChange = useCallback((e) => {
		const { name, value } = e.target;
		
		// Special handling for date changes to update time minimum
		if (name === 'scheduleDate') {
			setFormData((prev) => ({
				...prev,
				[name]: value,
				// Reset time if the new date is today and current time is invalid
				...(value === getMinDate() && prev.scheduleTime && 
					dayjs(`${value}T${prev.scheduleTime}`).isBefore(dayjs().add(15, 'minute')) && {
					scheduleTime: ''
				})
			}));
		} else {
			setFormData((prev) => ({
				...prev,
				[name]: value,
			}));
		}
	}, []);

	// Add handler for MediaPlaceholder
	const handleImageSelect = useCallback((selectedData) => {
		let data = Array.isArray(selectedData) ? selectedData[0] : selectedData;
		if (data !== undefined && data?.url) {
			setFormData(prev => ({
				...prev,
				image: data.url
			}));
		}
	}, []);

	const handleSubmit = useCallback(async (e) => {
		e.preventDefault();

		// Prevent duplicate submissions
		if (isSubmitting) {
			return;
		}

		setIsSubmitting(true);

		if (!projectId) {
			toast.error('Project ID is missing');
			setIsSubmitting(false);
			return;
		}
		const mappedAction = mapAction(formData?.action?.action, { ...formData?.action });
		try {
			// Handle image URL
			let imageUrl = null;
			if (formData?.image) {
				try {
					// Validate and normalize the URL
					const url = new URL(formData.image);
					imageUrl = url.href;
				} catch (error) {
					console.error('Invalid image URL:', error);
					toast.error('Invalid image URL provided');
					setIsSubmitting(false);
					return;
				}
			}

			// Prepare deep link data from action
			// const appDeepLink = formData.action ? {
			// 	resource: formData.action.resource || '',
			// 	id: formData.action.id || '',
			// 	title: formData.action.title || formData.title
			// } : {};
			const appDeepLink = mappedAction;
			if (formData.sendType === 'immediately') {
				// Prepare immediate notification data according to API structure
				// const notificationData = {
				// 	feature_id: 26, // Default feature ID for custom notifications
				// 	app_feature_id: 26,
				// 	icon_path: "123", // Default icon path
				// 	notification_type: "custom",
				// 	post_title: formData.title,
				// 	message: formData.message,
				// 	app_deep_link: appDeepLink,
				// 	timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // User's timezone
				// 	...(imageUrl && { image_url: imageUrl }),
				// 	title: formData.title
				// };
				const notificationData = {
					title: formData.title,
					message: formData.message,
					metadata: {
						...appDeepLink,
						image_url: imageUrl,
						app_feature_id: formData?.action ? encodeURIComponent(JSON.stringify(formData?.action)) : undefined,
					}
				}
				createNotificationMutation.mutate({
					projectId,
					data: notificationData,
				});
			} else {
				// Validate scheduled date and time
				const validation = validateScheduledDateTime(formData.scheduleDate, formData.scheduleTime);
				if (!validation.isValid) {
					toast.error(validation.error);
					setIsSubmitting(false);
					return;
				}

				// Validate end date for recurring schedules
				if (['daily', 'weekly', 'monthly'].includes(formData.scheduleType)) {
					if (!formData.scheduleEndDate) {
						toast.error('Please select an end date for recurring notifications');
						setIsSubmitting(false);
						return;
					}
					
					const startDate = dayjs(formData.scheduleDate);
					const endDate = dayjs(formData.scheduleEndDate);
					
					if (endDate.isBefore(startDate)) {
						toast.error('End date must be after start date');
						setIsSubmitting(false);
						return;
					}
				}

				// Prepare scheduled notification data
				const dateTime = formatDateTime(formData.scheduleDate, formData.scheduleTime);
				const endDateTime = formData.scheduleEndDate ?
					formatDateTime(formData.scheduleEndDate, formData.scheduleTime) : null;

				if (!dateTime) {
					toast.error('Please select a valid date and time');
					setIsSubmitting(false);
					return;
				}

				// const scheduledData = {
				// 	feature_id: "24", // Default feature ID for scheduled notifications
				// 	app_feature_id: "24.1441716117",
				// 	icon_path: "https://d3myyafggcycom.cloudfront.net/LIVE/diy/images/features/24/android-icon/icon.png?active=#000000&inactive=#959595",
				// 	notification_type: "custom",
				// 	post_title: formData.title,
				// 	message: formData.message,
				// 	app_deep_link: appDeepLink,
				// 	timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				// 	time: dateTime.time,
				// 	start_date: dateTime.date,
				// 	...(endDateTime && { end_date: endDateTime.date }),
				// 	frequency: formData.scheduleType,
				// 	...(imageUrl && { image_url: imageUrl }),
				// 	title: formData.title
				// };
				const scheduledData = {
					title: formData.title,
					message: formData.message,
					start_date: dateTime.date,
					end_date: endDateTime ? endDateTime.date : dateTime.date,
					time:  dateTime.time,
					app_feature_id: formData?.action ? encodeURIComponent(JSON.stringify(formData?.action)) : undefined,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					frequency: formData.scheduleType,
					metadata: {		
						image_url: imageUrl,
						// app_feature_id: formData?.action ? encodeURIComponent(JSON.stringify(formData?.action)) : undefined,
					}
				}

				createScheduledNotificationMutation.mutate({
					projectId,
					data: scheduledData,
				});
			}
		} catch (error) {
			toast.error('Failed to prepare notification data');
			console.error('Error preparing notification:', error);
			setIsSubmitting(false); // Reset submitting state on error
		}
	}, [formData, projectId, createNotificationMutation, createScheduledNotificationMutation, isSubmitting]);

	const handleTestNotification = useCallback(() => {
		if (!projectId) {
			toast.error('Project ID is missing');
			return;
		}

		// For test notifications, we need customer IDs
		// This will open the test notification modal to collect email addresses
		setIsTestNotificationOpen(true);
	}, [projectId]);

	// New function to handle actual test notification sending
	const sendTestNotification = useCallback((customerIds) => {
		if (!projectId || !customerIds || customerIds.length === 0) {
			toast.error('Please provide at least one email address');
			return;
		}
		const testData = {
			title: formData.title || 'Test Notification',
			body: formData.message || 'This is a test notification',
			action: formData.action?.action || "",
			resource: formData.action?.resource || "",
			resource_id: formData.action?.id || "",
			resource_title: formData.action?.title || formData.title,
			app_feature_id: "",
			feature_id: "",
			customer_ids: customerIds
		};

		sendTestNotificationMutation.mutate({
			projectId,
			data: testData,
		});
	}, [formData, projectId, sendTestNotificationMutation]);

	// Memoized send type options
	const sendTypeOptions = useMemo(() => [
		{ value: 'immediately', label: 'Send Immediately' },
		{ value: 'schedule', label: 'Schedule for Later' },
	], []);

	// Memoized actions schema
	const actions = useMemo(() => appmaker.actions.getAllActions(), []);
	const schema = actions;

	// TODO: Check if the app is configured
	const isConfigured = false;

	return (
		<div className="p-6">
			<div className="mb-6">
				{/* {!isConfigured && (
					<div className="mb-2 text-sm text-gray-800 bg-red-100 p-2 rounded-md border border-red-300 border-dashed flex flex-row items-center justify-center gap-2">
						<ExclamationIcon className="w-6 h-6 text-red-700" />
						<p>
							Push Notifications can only be sent once your app is live. For
							more information on launching your app, please{' '}
							<a
								href="#" //TODO: Add link
								className="text-indigo-600 underline"
							>
								click here
							</a>
							.
						</p>
					</div>
				)} */}
				<div>
					<h2 className="text-2xl font-medium text-gray-900">
						New Notification
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						Create and send new notifications to your users
					</p>
				</div>
			</div>

			<div className="flex gap-8">
				{/* Form Section */}
				<div className="flex-1 p-0">
					<form className="space-y-6" onSubmit={handleSubmit}>
						<TextInput
							id="title"
							name="title"
							label="Message Title"
							value={formData.title}
							onChange={handleInputChange}
							placeholder="Eg: New product launch <3"
							required
							helpText={`Press ${isAppleOS() ? '⌘ + Ctrl + Space' : 'Win + .'} to insert emojis`}
						/>

						<TextArea
							id="message"
							name="message"
							label="Message"
							value={formData.message}
							onChange={handleInputChange}
							placeholder="Eg: We have exclusive access to the new product launch. Get it now!"
							required
						/>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Select Action
							</label>
							<AppmakerForm
								key={formKey} // Force remount when form is reset
								options={actions}
								value={{
									formData: formData.action || {},
									schema: actions
								}}
								optionKey={'action'}
								setValue={({ formData: actionData }) => {
									setFormData(prev => ({
										...prev,
										action: actionData
									}));
								}}
								formVersion={2}
								debug={false}
							/>
						</div>

						<RadioGroup
							name="sendType"
							label="Send Type"
							options={sendTypeOptions}
							value={formData.sendType}
							onChange={handleInputChange}
						/>

						{formData.sendType === 'schedule' && (
							<div>
								<div className="flex items-center justify-between mt-4">
									<label
										htmlFor="scheduleType"
										className="block text-sm font-medium text-gray-700"
									>
										Choose when to send
									</label>
									<div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
										⚠️ Minimum 15 minutes ahead
									</div>
								</div>
								<select
									id="scheduleType"
									name="scheduleType"
									value={formData.scheduleType}
									onChange={handleInputChange}
									className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								>
									{scheduleTypes.map((type) => (
										<option key={type.value} value={type.value}>
											{type.label}
										</option>
									))}
								</select>

								{formData.scheduleType === 'once' && (
									<div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
										<div>
											<label
												htmlFor="scheduleDate"
												className="block text-sm font-medium text-gray-700"
											>
												Date
											</label>
											<input
												type="date"
												id="scheduleDate"
												name="scheduleDate"
												value={formData.scheduleDate}
												onChange={handleInputChange}
												min={getMinDate()}
												className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												required
											/>
											<p className="mt-1 text-xs text-gray-500">
												Select today or a future date
											</p>
										</div>
										<div>
											<label
												htmlFor="scheduleTime"
												className="block text-sm font-medium text-gray-700"
											>
												Time
											</label>
											<input
												type="time"
												id="scheduleTime"
												name="scheduleTime"
												value={formData.scheduleTime}
												onChange={handleInputChange}
												min={getMinTimeForDate(formData.scheduleDate)}
												className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												required
											/>
											<p className="mt-1 text-xs text-gray-500">
												{formData.scheduleDate === getMinDate() 
													? 'Must be at least 15 minutes from now'
													: 'Select any time'
												}
											</p>
										</div>
									</div>
								)}

								{['daily', 'weekly', 'monthly'].includes(
									formData.scheduleType
								) && (
										<div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
											<div>
												<label
													htmlFor="scheduleDate"
													className="block text-sm font-medium text-gray-700"
												>
													Start Date
												</label>
												<input
													type="date"
													id="scheduleDate"
													name="scheduleDate"
													value={formData.scheduleDate}
													onChange={handleInputChange}
													min={getMinDate()}
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
													required
												/>
												<p className="mt-1 text-xs text-gray-500">
													Select today or a future date
												</p>
											</div>
											<div>
												<label
													htmlFor="scheduleTime"
													className="block text-sm font-medium text-gray-700"
												>
													Start Time
												</label>
												<input
													type="time"
													id="scheduleTime"
													name="scheduleTime"
													value={formData.scheduleTime}
													onChange={handleInputChange}
													min={getMinTimeForDate(formData.scheduleDate)}
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
													required
												/>
												<p className="mt-1 text-xs text-gray-500">
													{formData.scheduleDate === getMinDate() 
														? 'Must be at least 15 minutes from now'
														: 'Select any time'
													}
												</p>
											</div>
											<div className="sm:col-span-2">
												<label
													htmlFor="scheduleEndDate"
													className="block text-sm font-medium text-gray-700"
												>
													End Date
												</label>
												<input
													type="date"
													id="scheduleEndDate"
													name="scheduleEndDate"
													value={formData.scheduleEndDate}
													onChange={handleInputChange}
													min={formData.scheduleDate || getMinDate()}
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
													required
												/>
												<p className="mt-1 text-xs text-gray-500">
													Must be after start date
												</p>
											</div>
										</div>
									)}
							</div>
						)}

						<div className="mt-10">
							<MediaPlaceholder
								onSelect={handleImageSelect}
								allowedTypes={['image']}
								multiple={false}
								onSelectURL={(data) => {
									try {
										// Validate URL before setting
										new URL(data);
										setFormData(prev => ({
											...prev,
											image: data
										}));
									} catch (error) {
										console.error('Invalid URL:', error);
										toast.error('Please provide a valid image URL');
									}
								}}
								attributes={{
									icon: PhotographIcon,
									title: 'Push Image',
									description: "Showcase a product that's on sale or highlight your best offer by adding an image in push notification",
								}}
								forceRender={true}
							>
								{formData.image ? (
									<img
										src={formData.image}
										alt="push"
										width={50}
										height={50}
										className="object-cover pointer-events-none h-12 w-12 rounded-lg mt-3"
										onError={(e) => {
											console.error('Error loading image:', e);
											toast.error('Failed to load image preview');
											setFormData(prev => ({
												...prev,
												image: null
											}));
										}}
									/>
								) : null}
							</MediaPlaceholder>
						</div>

						{/* Admin-only switch to keep form after sending */}
						{checkIsAdmin() && (
							<div className="mt-6">
								<AdminSwitch
									checked={keepFormAfterSending}
									onChange={setKeepFormAfterSending}
									label="Keep form data after sending"
									description="When enabled, the form will not be cleared after successfully sending a notification."
								/>
							</div>
						)}
					</form>
				</div>

				{/* Preview Section */}
				<PreviewSection
					formData={formData}
					onTestNotification={handleTestNotification}
					onSendNotification={handleSubmit}
					isTestLoading={sendTestNotificationMutation.isLoading}
					isSendLoading={isSubmitting || createNotificationMutation.isLoading || createScheduledNotificationMutation.isLoading}
				/>
			</div>
			<TestNotification
				isOpen={isTestNotificationOpen}
				setIsOpen={setIsTestNotificationOpen}
				onSendTest={sendTestNotification}
				projectId={projectId}
			/>
		</div>
	);
};

export default React.memo(NewNotification);
